<template>
  <div class="mt-4">
    <p v-if="loading && !state" class="text-sm text-gray-500">
      Loading credits…
    </p>
    <p v-else-if="!state" class="text-sm text-gray-500">
      Couldn't load the credits.
      <button type="button" class="underline" @click="load">Retry</button>
    </p>
    <template v-else>
      <dl class="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div class="rounded-lg border border-gray-200 p-4">
          <dt class="text-sm text-gray-500">Dashboard credits (packs)</dt>
          <dd class="mt-1 text-lg font-medium">
            {{ format(state.pack_total) }}
          </dd>
        </div>
        <div class="rounded-lg border border-gray-200 p-4">
          <dt class="text-sm text-gray-500">Real balance</dt>
          <dd class="mt-1 text-lg font-medium">{{ format(state.balance) }}</dd>
        </div>
        <div
          :class="[
            'rounded-lg border p-4',
            hasGap ? 'border-red-300 bg-red-50' : 'border-gray-200',
          ]"
        >
          <dt class="text-sm text-gray-500">Gap</dt>
          <dd
            :class="[
              'mt-1 text-lg font-medium',
              hasGap ? 'text-red-600' : 'text-gray-900',
            ]"
          >
            {{ signed(state.gap) }}
          </dd>
        </div>
      </dl>

      <p
        v-if="state.pack_owner_id !== state.user_id"
        class="mt-3 text-sm text-gray-500"
      >
        This user shares the organization's credits, so the packs belong to the
        parent user (#{{ state.pack_owner_id }}).
      </p>
      <p v-if="!state.use_billing_report" class="mt-3 text-sm text-gray-500">
        This organization doesn't use credit packs; only the balance applies.
      </p>
      <p
        v-if="state.pending_expiry_credits > 0"
        class="mt-3 text-sm text-yellow-700"
      >
        {{ format(state.pending_expiry_credits) }} credits are in packs past
        their expiry date that the nightly expiry run (11:58 PM) hasn't
        processed yet, so part of this gap may not be drift. Sync after it runs.
      </p>

      <table
        v-if="state.packs.length"
        class="mt-6 min-w-full divide-y divide-gray-200 text-sm"
      >
        <thead>
          <tr class="text-left text-gray-500">
            <th class="py-2 pr-4 font-medium">Pack</th>
            <th class="py-2 pr-4 font-medium">Source</th>
            <th class="py-2 pr-4 font-medium text-right">Remaining</th>
            <th class="py-2 pr-4 font-medium text-right">Credits</th>
            <th class="py-2 font-medium">Expires</th>
          </tr>
        </thead>
        <tbody class="divide-y divide-gray-100">
          <tr v-for="pack in state.packs" :key="pack.id">
            <td class="py-2 pr-4">
              #{{ pack.id }}<span v-if="pack.is_free"> (free)</span>
            </td>
            <td class="py-2 pr-4">{{ pack.source }}</td>
            <td class="py-2 pr-4 text-right">{{ format(pack.remaining) }}</td>
            <td class="py-2 pr-4 text-right">{{ format(pack.credits) }}</td>
            <td class="py-2">{{ date(pack.expires_at) }}</td>
          </tr>
        </tbody>
      </table>

      <form
        v-if="
          hasGap && state.use_billing_report && !state.pending_expiry_credits
        "
        @submit.prevent="onSubmit"
        class="mt-6 flex flex-col items-start sm:max-w-lg"
      >
        <label for="syncDirection" class="text-sm font-medium text-gray-700">
          Direction
        </label>
        <select
          id="syncDirection"
          v-model="direction"
          class="form-field-no-icon sm:w-full mt-1"
        >
          <option value="packs_to_balance">Match packs to balance</option>
          <option value="balance_to_packs">Match balance to packs</option>
        </select>
        <p class="text-sm text-gray-500 mt-1">{{ directionHint }}</p>

        <label for="syncComment" class="text-sm font-medium text-gray-700 mt-4">
          Reason
        </label>
        <input
          id="syncComment"
          type="text"
          v-model="comment"
          class="form-field-no-icon sm:w-full outline-none mt-1"
          placeholder="sc-123456 why the numbers are being synced"
          @input="formActions.clearFieldError('comment')"
          required
        />
        <p class="text-red-500 text-sm mt-1">{{ errors.comment }}</p>

        <button
          type="submit"
          :disabled="!comment.trim() || submitting"
          class="button-submit mt-5"
        >
          Sync credits
        </button>
      </form>
    </template>
  </div>
</template>
<script>
import { ref } from "vue";
import humanize from "humanize";
import formMixin from "@/mixins/formMixin";
import eventBus from "@/utils/eventBus";
import useFormErrors from "@/api/formErrors";
import useConfirmationModal from "@/api/confirmationModal";

export default {
  name: "UserCreditsSyncAdmin",
  mixins: [formMixin],
  props: {
    userId: Number,
  },
  setup() {
    const { errors, formActions } = useFormErrors();
    return {
      errors,
      formActions,
      state: ref(null),
      loading: ref(false),
      direction: ref("packs_to_balance"),
      comment: ref(""),
      submitting: ref(false),
    };
  },
  computed: {
    hasGap() {
      return this.state && this.state.gap !== 0;
    },
    after() {
      if (this.direction === "packs_to_balance") {
        return { pack_total: this.state.balance, balance: this.state.balance };
      }
      return {
        pack_total: this.state.pack_total,
        balance: this.state.pack_total,
      };
    },
    directionHint() {
      if (this.direction === "packs_to_balance") {
        return this.state.gap > 0
          ? "Deducts the gap from the packs (free packs first, then oldest). The balance is not changed."
          : "Adds a correction pack for the gap. The balance is not changed.";
      }
      return "Changes the balance to the pack total. The packs are not changed.";
    },
  },
  async mounted() {
    await this.load();
  },
  methods: {
    format(value) {
      return humanize.numberFormat(value, 0);
    },
    signed(value) {
      return `${value > 0 ? "+" : ""}${this.format(value)}`;
    },
    date(value) {
      return value ? new Date(value).toLocaleDateString() : "—";
    },
    async load() {
      this.loading = true;
      try {
        this.state =
          (await this.handleSubmissionErrors(
            () => this.$alfredService.adminUserCreditsBalance(this.userId),
            {}
          )) || null;
      } finally {
        this.loading = false;
      }
    },
    async onSubmit() {
      const modal = useConfirmationModal();
      await modal.openModal({
        title: "Sync credits?",
        message:
          `Packs ${this.format(this.state.pack_total)} → ${this.format(
            this.after.pack_total
          )}, ` +
          `balance ${this.format(this.state.balance)} → ${this.format(
            this.after.balance
          )}. ` +
          "This is recorded with your name and the reason.",
        onSubmit: () => this.handleSync(),
      });
    },
    async handleSync() {
      this.submitting = true;
      let response;
      try {
        response = await this.handleSubmissionErrors(
          () =>
            this.$alfredService.syncUserCredits({
              user_id: this.userId,
              direction: this.direction,
              expected_pack_owner_id: this.state.pack_owner_id,
              expected_pack_total: this.state.pack_total,
              expected_balance: this.state.balance,
              comment: this.comment.trim(),
            }),
          { formActions: this.formActions, throwExceptions: true }
        );
      } catch (e) {
        // 409: the numbers moved since they were loaded. The error toast
        // says so; reload so the admin can review the new values and retry.
        if (e?.response?.status === 409) {
          await this.load();
        }
        return;
      } finally {
        this.submitting = false;
      }
      eventBus.trigger("notify", {
        notification: {
          group: "top-right",
          type: "success",
          text:
            `Credits synced: packs ${this.format(
              response.pack_total_after
            )}, ` + `balance ${this.format(response.balance_after)}`,
        },
      });
      this.comment = "sc-";
      await this.load();
      await this.$store.dispatch("user/FETCH_USER", { userId: this.userId });
    },
  },
};
</script>
